import { platformApiClient } from './platform-api-client';
import type {
  CreatePlatformUserRequest,
  PlatformUserSummary,
  RevokeSessionsResult,
  UpdatePlatformUserActiveRequest,
  UpdatePlatformUserIdentityRequest,
  UpdatePlatformUserRoleRequest,
} from '@/types/platform-user.types';

/**
 * Phase 12.6-3B — the Platform Users API client.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §6, §4.7.
 * Backend contract: six routes from Phase 12.1C (SP-16) plus `PATCH :id` from
 * 12.6-1 — **seven in total**, all pinned in the frozen route-authorization
 * snapshot and verified over real HTTP in 12.6-2.
 *
 * ── Envelope reminder ───────────────────────────────────────────────────────
 * `platformApiClient` already unwraps `{ data, meta }`, so `r.data` IS the
 * payload. A second `.data` access returns `undefined` — see CLAUDE.md. Its
 * `baseURL` is `/api/v1`, so paths here are written without that prefix.
 *
 * ── The list takes NO parameters ────────────────────────────────────────────
 * `GET /platform/users` accepts no query parameters at all and returns a **bare
 * array**, ordered `isActive desc, email asc`. It is not paginated and there is
 * no `{ items, total, limit, offset }` envelope — unlike `/platform/tenants`.
 *
 * That is why this service has no `list(params)`: sending `?search=` would be
 * silently ignored by the server, producing a search box that quietly lies.
 * §5.3 of the design decides search, filtering and sorting are **client-side**
 * for this bounded staff table, and records that server-side search would be a
 * new backend requirement — i.e. out of scope. Adding a parameter here without
 * that backend work would invent an endpoint contract.
 *
 * ── Authorization is the backend's, not this file's ─────────────────────────
 * Reads need `platform:platform-users:view` (owner, admin, auditor); every
 * mutation needs `platform:platform-users:manage`, which D-041 §5.2 grants to
 * `platform-owner` ALONE. This client does not check permissions and must not:
 * `canPlatform()` decides what to *draw*, `PlatformPermissionGuard` decides what
 * is *allowed*. A call made without authority returns 403, which callers must
 * surface rather than prevent by hiding a button alone.
 *
 * ── Deliberately absent ─────────────────────────────────────────────────────
 * No invitation, no admin-initiated password reset, no first-login setup, no
 * account recovery, no delete. All five were declined by OD-B and **no backend
 * route exists** — 12.6-2 proved each returns 404 even for an owner. A method
 * here would be an invented endpoint.
 */
export const platformUserService = {
  /**
   * `GET /platform/users` — the full roster. Requires `:view`.
   *
   * Guarded with `Array.isArray` per the response-envelope convention: a
   * non-array body (an error shape that slipped through, a changed contract)
   * yields an empty list rather than crashing a `.map()` in the caller.
   */
  list: (): Promise<PlatformUserSummary[]> =>
    platformApiClient
      .get<PlatformUserSummary[]>('/platform/users')
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  /** `GET /platform/users/:id` — one user. Requires `:view`. 404 if unknown. */
  getById: (id: string): Promise<PlatformUserSummary> =>
    platformApiClient.get<PlatformUserSummary>(`/platform/users/${id}`).then((r) => r.data),

  /**
   * `POST /platform/users` — create. Requires `:manage`. **201** on success.
   *
   * The password is write-only: it is never returned, never logged and never
   * audited. A duplicate email or username is a typed **409**, not a raw
   * database error; an invalid role or a password under 12 characters is a 400
   * from the validation pipe.
   */
  create: (payload: CreatePlatformUserRequest): Promise<PlatformUserSummary> =>
    platformApiClient.post<PlatformUserSummary>('/platform/users', payload).then((r) => r.data),

  /**
   * `PATCH /platform/users/:id` — identity edit (12.6-1, OD-B). Requires `:manage`.
   *
   * Accepts `displayName`, `email` and `username` only. Sending `role` or
   * `isActive` is a **400** (`forbidNonWhitelisted`), not a silently dropped
   * field — those keep their own routes, which carry the self-mutation guards.
   *
   * **Changing `email` terminates that user's sessions**: it bumps
   * `tokenVersion` and revokes every refresh token, because the email is the
   * platform sign-in identifier. `displayName` and `username` changes revoke
   * nothing. Self-editing is permitted — but editing your OWN email signs you
   * out, so the caller must confirm first.
   *
   * A no-op edit (every submitted value already current, after normalisation)
   * writes nothing and audits nothing, and still returns the current row.
   */
  updateIdentity: (
    id: string,
    payload: UpdatePlatformUserIdentityRequest,
  ): Promise<PlatformUserSummary> =>
    platformApiClient
      .patch<PlatformUserSummary>(`/platform/users/${id}`, payload)
      .then((r) => r.data),

  /**
   * `PATCH /platform/users/:id/role` — change authority. Requires `:manage`.
   *
   * **Refused against yourself** (403-equivalent from the service): changing
   * your own role is either escalation or self-lockout. Bumps `tokenVersion`
   * and revokes all refresh tokens, so a demotion takes effect immediately
   * rather than after the old access token expires.
   */
  changeRole: (id: string, payload: UpdatePlatformUserRoleRequest): Promise<PlatformUserSummary> =>
    platformApiClient
      .patch<PlatformUserSummary>(`/platform/users/${id}/role`, payload)
      .then((r) => r.data),

  /**
   * `PATCH /platform/users/:id/active` — activate or deactivate. Requires `:manage`.
   *
   * **Refused against yourself.** Deactivation bumps `tokenVersion` and revokes
   * refresh tokens, so access ends at once; activation does neither, because a
   * deactivated account has no live sessions to revoke.
   */
  setActive: (id: string, payload: UpdatePlatformUserActiveRequest): Promise<PlatformUserSummary> =>
    platformApiClient
      .patch<PlatformUserSummary>(`/platform/users/${id}/active`, payload)
      .then((r) => r.data),

  /**
   * `POST /platform/users/:id/revoke-sessions` — forced sign-out. Requires `:manage`.
   *
   * The one route in this group that does NOT return a user — it returns
   * `{ message }`, so callers must refetch the row rather than expect one back.
   *
   * **Permitted against yourself**, unlike role and status changes: signing your
   * own sessions out is a legitimate response to a suspected compromise, not an
   * escalation. The caller must warn that it signs the operator out.
   */
  revokeSessions: (id: string): Promise<RevokeSessionsResult> =>
    platformApiClient
      .post<RevokeSessionsResult>(`/platform/users/${id}/revoke-sessions`)
      .then((r) => r.data),
};
